import { useEffect, useRef, useState } from 'react';

/**
 * Automatické ukládání rozpracovaného konceptu formuláře do prohlížeče.
 *
 * Formulářové stránky drží svůj stav jen v paměti (`useState`). Když uživatel
 * zavře záložku nebo omylem reloadne, aniž stiskl „Uložit", data byla dosud
 * nenávratně pryč. Tento hook průběžně (debounced) zrcadlí snímek dat do
 * `localStorage` a při příštím otevření prázdného formuláře nabídne obnovení.
 *
 * Ukládá se JEN u nového formuláře (`enabled` = když se needituje záznam
 * z archivu). Při editaci z archivu je zdrojem pravdy uložený dokument, takže
 * koncept se nezakládá ani nenabízí.
 *
 * Návratové hodnoty:
 *  - `pending`  — koncept nalezený při načtení (nebo `null`); pro banner „Obnovit".
 *  - `persist`  — zavolej při každé změně dat (typicky v `useEffect`).
 *  - `accept`   — zavolej po „Obnovit" (skryje nabídku; data restoruje volající).
 *  - `clear`    — zavolej po „Zahodit" i po ÚSPĚŠNÉM uložení (smaže koncept).
 */
const PREFIX = 'sitedoc:draft:';
const DEBOUNCE_MS = 600;

export interface DocDraft<T> {
  pending: { data: T; savedAt: number } | null;
  persist: (snapshot: T) => void;
  accept: () => void;
  clear: () => void;
}

export function useDocDraft<T>(key: string, enabled: boolean): DocDraft<T> {
  const storageKey = PREFIX + key;
  const [pending, setPending] = useState<{ data: T; savedAt: number } | null>(null);
  const timer = useRef<number | null>(null);
  // Přeskoč úplně první `persist` (mount s výchozími prázdnými daty), ať se
  // nezakládá prázdný koncept a hlavně ať se nepřepíše existující koncept dřív,
  // než ho uživatel stihne obnovit.
  const primed = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as { data?: T; savedAt?: number };
        if (parsed && parsed.data) {
          setPending({ data: parsed.data, savedAt: parsed.savedAt ?? 0 });
        }
      }
    } catch {
      /* poškozený JSON / nedostupné úložiště — tiše ignoruj */
    }
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
    // Jen jednou při mountu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = (snapshot: T) => {
    if (!enabled) return;
    if (!primed.current) {
      primed.current = true;
      return;
    }
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({ data: snapshot, savedAt: Date.now() }),
        );
      } catch {
        /* localStorage plný nebo privátní režim — koncept prostě neuložíme */
      }
    }, DEBOUNCE_MS);
  };

  const clear = () => {
    if (timer.current) window.clearTimeout(timer.current);
    try {
      localStorage.removeItem(storageKey);
    } catch {
      /* ignoruj */
    }
    setPending(null);
  };

  const accept = () => setPending(null);

  return { pending, persist, accept, clear };
}
